import { TrackColor } from "../render/trackColor";
import {
    AbstractHorizontalContainer,
    HorizontalNoteContainer
} from "./containers";
import {
    ComputedContextFrame,
    ContextCell,
    ContextPlane,
} from "./context";
import { RydInstrument } from "./instrument";
import { RydMixer } from "../audio/mixer";
import _ from "lodash";
import {
    RydTrack,
    RydTrackUIState
} from "./tracks";
import { MetronomeMarking, NoteDuration, TimeSignature } from "./core";
import { GridResolution } from "./grid";
import { ContainerSerializer } from "../services/ContainerSerializer";
import {
    RydBookmark,
    RydBookmarkSelection
} from "./bookmark";
import { assert } from "../utils/error";
import { RydInputSource } from "../audio/input";


export type ProjectTracksMap = Map<number, RydTrack>;
export type ProjectUIState = {
    tracks: Map<number, RydTrackUIState>,
    zoomFactor: number,
    gridResolution: GridResolution,
    snapToGrid: boolean,
    newNoteDuration: NoteDuration,
    // FIXME: separate these properties out of ProjectUIState; they don't need to be persisted to the project
    editMode: 'NOTES' | 'DRAW' | 'SELECT',
    globalKeybindingsSuspended: boolean,
};
export type ProjectMixerState = {
    master: {
        gain: number,
    },
    tracks: Map<number, RydMixer.TrackInitParams>
};
export type ProjectInstrumentAssignmentMap = Map<number, RydInstrument>;
export type ProjectSourceAssingmentMap = Map<number, RydInputSource>;

export class RydProject {
    name: string;
    contextFrame: {
        grid: ContextPlane,
        computed: ComputedContextFrame
    };
    tracks: ProjectTracksMap = new Map();
    // FIXME what is this doing with selection?
    bookmarks: (RydBookmark & Partial<RydBookmarkSelection>)[] = [];
    private lines: AbstractHorizontalContainer<any>[] = [];
    instrumentAssignments: ProjectInstrumentAssignmentMap = new Map();
    sourceAssignments: ProjectSourceAssingmentMap = new Map();
    uiState: ProjectUIState;
    mixerState: ProjectMixerState;

    private constructor() { }

    static createForNewProject(
        name: string,
        addInitialTrack: boolean = false
    ) {
        const ppq = 480;
        const initialTrackLength = ppq * 32;
        const il = new RydProject();
        il.name = name;
        il.contextFrame = {
            grid: new ContextPlane(initialTrackLength, 1),
            computed: ComputedContextFrame.forNewProject()
        }

        // add initial MetronomeMarking and TimeSignature
        il.contextFrame.grid.addRow();
        const metronomeCell = new ContextCell(0, initialTrackLength);
        metronomeCell.properties.metronome = new MetronomeMarking(120, new NoteDuration(16));
        il.contextFrame.grid.addCellToRow(0, metronomeCell);
        const timeSignatureCell = new ContextCell(0, initialTrackLength);
        timeSignatureCell.properties.timeSignature = new TimeSignature(4, 4);
        il.contextFrame.grid.addCellToRow(1, timeSignatureCell);

        // UI state
        il.uiState = {
            tracks: new Map(),
            zoomFactor: ppq,
            gridResolution: 'bar',
            snapToGrid: true,
            newNoteDuration: NoteDuration.fromBackendValue('QUARTER'),
            editMode: 'SELECT',
            globalKeybindingsSuspended: false,
        };
        il.mixerState = {
            master: {
                gain: 1.0,
            },
            tracks: new Map(),
        };
        if (addInitialTrack) {
            il.put(0, new HorizontalNoteContainer(
                {
                    length: initialTrackLength,
                    ppq: ppq
                }),
                { skipLengthCheck: true },
            );
        }

        // by default bookmarks for start and end of project.
        il.bookmarks = [
            {
                color: '#ff0000',
                position: 0,
                note: 'Default playback start',
                scope: 'app',
                label: 'start',
            },
            {
                color: '#00ff00',
                position: il.getLength(),
                note: 'Default playback end',
                scope: 'app',
                label: 'end',
            }
        ];

        return il;
    }

    get(index: number) {
        return this.lines[index];
    }

    put(
        index: number,
        line: AbstractHorizontalContainer<any>,
        opts: { skipLengthCheck: boolean } = { skipLengthCheck: false },
        track?: Partial<RydTrack>,
    ) {
        if (!opts.skipLengthCheck && this.lines.length > 0) {
            assert(this.getLength() === line.getLength(), `RydProject.put: line length mismatch: ${this.getLength()} != ${line.getLength()}`);
        }

        this.tracks.set(index, {
            id: index,
            name: track?.name ? track.name : `track-${index}`,
            instrument: undefined,
            inputSource: undefined,
            length: line.getLength(),
            color: TrackColor.getRandomColor(),
            sequence: line as HorizontalNoteContainer,
            bookmarks: track?.bookmarks ? track.bookmarks : [],
        });

        this.lines[index] = line;
        this.uiState.tracks.set(index, {
            trackId: index,
            collapsed: true,
            noteRange: { min: 0, max: 107 },
            tonalityVisible: false,
            color: TrackColor.getRandomColor(),
        });

        if (line instanceof HorizontalNoteContainer) {
            this.mixerState.tracks.set(index, {
                id: index,
                name: track?.name ? track.name : `track-${index}`,
                gain: 1.0,
                pan: 0.0,
                muted: false,
                solo: false,
                // sourceNode: line.getOutputNode(),
                onBus: true
            });
        }
    }

    getTrack(index: number): RydTrack | undefined {
        return this.tracks[index];
    }

    size() {
        return this.lines.length;
    }

    getAll(): AbstractHorizontalContainer<any>[] {
        return this.lines;
    }

    getLength() {
        return Math.max(...this.lines.map(l => l.getLength()), 0);
    }

    setLength(length: number) {
        this.lines.forEach(line => line.setLength(length));
    }
}

export function findLargestPPQ(
    line: RydProject
): number {
    return line.getAll().map(l => l.getPPQ()).reduce((a, b) => Math.max(a, b), 1);
}

export function generateSaveFile(project: RydProject): { lineJSON: string, saveFileName: string } {
    const lineJSON = ContainerSerializer.serializeProject(project);
    const saveFileName = `savefile-${new Date().toISOString().replace(':', '-')}.ryd`;
    return { lineJSON, saveFileName };
}

export function readProjectFromFile(
    contents: string | ArrayBuffer,
    audioContext: AudioContext
) {
    const json = JSON.parse(contents as string);
    return ContainerSerializer.loadInputLineSaveFile(json, audioContext);
}

/** @deprecated */
export function assignmentsEqual(a: Map<number, RydInstrument>, b: Map<number, RydInstrument>) {
    if (a.size !== b.size) {
        return false;
    }
    const x = Array.from(a.entries());
    // const y = Array.from(b.entries());
    return x.map(([key, value]) => {
        if (!b.has(key) || !_.isEqual(value, b.get(key))) {
            return false;
        }
        return true;
    }).reduce((acc, curr) => {
        return acc && curr;
    }, true);
}

